import * as Alkali from 'alkali'
import { all, reactive, Label, Checkbox, A, Div } from 'alkali'
import Dropdown from '../generic/Dropdown'
import { SortingDropdown } from './SortingDropdown'
import { AutoSelectDialog, selectAllModeVar } from './AutoSelectDialog'
import { selectedOutcomes, groupedInterventions } from 'model/EvidenceSearch'
import { defaultStudyExpanded, tabFilter, activeTab } from 'model/StudyCollection'
import { sorting, currentAnalysisSelections, faqOrReadOnly, docSearchQuery } from 'model/SearchQuery'
import * as controlIcon from 'icons/pill.svg'
import * as sortIcon from 'icons/data-transfer-vertical.svg'
import * as filterIcon from 'icons/filter-1.svg'
import * as dropdownIcon from 'icons/arrow-down-1.svg'
import * as monitorPositiveIcon from 'icons/monitor-heart-rate-up.svg'

import Icon from 'components/Icon'
const recencySortingDropdown = new SortingDropdown({
	priorityDescription: 'More Recent',
	priorityTitle: 'Slide to the right to increase preference of retrieval of studies that were published more recently.',
	value: sorting.recency,
})
const participantsSortingDropdown = new SortingDropdown({
	priorityDescription: 'More Participants',
	priorityTitle: 'Slide to the right to increase preference of retrieval of studies that have more participants.',
	value: sorting.participants,
})
export let selectionChanged = reactive(false)
export let expandedConditions = reactive(false)
export let expandedInterventions = reactive(false)

let addUncertainStudies = reactive(false)
const rejectedFilterOptions = ['Wrong study design', 'Wrong population', 'Wrong intervention', 'Wrong outcome', 'Any reason']
const filterOptions = ['Included', 'Uncertain', 'Included + Uncertain', 'Selected']
const screeningFilterOptions = ['Included', 'Uncertain', 'Selected', 'Included + Uncertain', 'Excluded', 'Excluded + Uncertain']

const screeningTabFilterDropdown = new Dropdown({
	items: [...screeningFilterOptions.valueOf().map((filter) => (
			filter == 'Excluded' || filter == 'Excluded + Uncertain'
				? A('.rejected-filter-control', {
					onclick(event) {
						if (this.childDropdown) {
							this.childDropdown.hide()
							return this.childDropdown = null
						}
						// sub-dropdown to filter for rejected studies
						this.childDropdown = new Dropdown({
							items: [...rejectedFilterOptions.map((rejectedFilter) => ({
									content: Label(`.rejected-filter-dropdown`, [
										Checkbox({
											checked: filter == 'Excluded + Uncertain'
												? tabFilter.valueOf() == `${rejectedFilter} + Uncertain`
												: tabFilter.valueOf() == rejectedFilter,
											onclick(event) {
												event.preventDefault()
												if (filter == 'Excluded + Uncertain') {
													tabFilter.valueOf() == `${rejectedFilter} + Uncertain`
														? tabFilter.put('')
														: tabFilter.put(`${rejectedFilter} + Uncertain`)
												} else {
													tabFilter.valueOf() == rejectedFilter
														? tabFilter.put('')
														: tabFilter.put(rejectedFilter)
												}
											}
										}), ' ',
										rejectedFilter
									]),
								}))
							],
							closeOnClick: true
						})
						this.childDropdown.show(event, event.target)
					}
				}, [Icon('.icon-dropdown', { icon: dropdownIcon}), filter])
				: Label('.tab-filter-dropdown', [
					Checkbox({
						checked: tabFilter.to(currentFilter => currentFilter == filter),
						onchange() {
							tabFilter.valueOf() == filter
								? tabFilter.put('')
								: tabFilter.put(filter)
							screeningTabFilterDropdown.hide()
						}
					}), ' ', 
					filter
				])				
	))],
	closeOnClick: false
})
const tabFilterDropdown = new Dropdown({
	items: [...filterOptions.valueOf().map((filter) => (
		Label('.tab-filter-dropdown', [
				Checkbox({
					checked: tabFilter.to(currentFilter => currentFilter == filter),
					closeOnClick: true,
					onchange() {
						tabFilter.valueOf() == filter
							? tabFilter.put('')
							: tabFilter.put(filter)
						tabFilterDropdown.hide()
					}
				}), ' ', 
				filter
			])				
	))],
	closeOnClick: false
})

export const StudyHeader = (
	<section class="study">
		<header class="study-head" classes={{
				'selection-changed': selectionChanged,
				expanded: defaultStudyExpanded,
			}}>
			<div class="study-head-item study-toggle" title={selectionChanged.to(selectionChanged =>
				selectionChanged ? 'Search has changed, review selected items' :
				selectAllModeVar.to(mode => mode == 'likely' ? 'Select most likely analyzable studies' :
					mode == 'weighted' ? 'Select analyzable studies weighted by matching likelihood/heterogeneity' :
					'Select analyzable studies')) } />
			<div class="study-head-item study-expander"></div>
			{/* tab filter control */}
			{activeTab.to((tab) => (
				tab == 'screening' || tab == 'extract' || tab == 'analyzable'
					? <Icon 
						class="study-head-item tab-filter" 
						title="Filter study results by screening or selected state" 
						icon={ filterIcon } 
						onclick={(event) => {
							activeTab.valueOf() == 'extract' || activeTab.valueOf() == 'analyzable'
								? tabFilterDropdown.show(event, event.target)
								: screeningTabFilterDropdown.show(event, event.target)
						}} 
					/>
					: <Div class="study-head-item tab-filter" />
			))}
			<div class="study-head-item study-year-name">
				<div
					class="study-head-item study-year"
					title={faqOrReadOnly.to(readOnly => readOnly ? '' : "Click to select priority of recent studies")}
					onclick={event => {
						if (faqOrReadOnly.valueOf() || docSearchQuery.valueOf()?.query)
							return
						recencySortingDropdown.show(event, event.target)
					}}>
					<button class="icon-button">
						Year
						<Icon class="sort-icon" icon={ sortIcon } />
					</button>
				</div>
				<div class="study-head-item study-name">Name</div>
			</div>
			<div
				class="study-head-item study-N"
				title={faqOrReadOnly.to(readOnly => readOnly ? '' : "Click to select priority of studies with more participants")}
				onclick={event => {
					if (faqOrReadOnly.valueOf() || docSearchQuery.valueOf()?.query)
						return
					participantsSortingDropdown.show(event, event.target)
				}}>
				<button class="icon-button">
					N
					<Icon class="sort-icon" icon={ sortIcon } />
				</button>
			</div>
			{ all([ groupedInterventions, selectedOutcomes, currentAnalysisSelections.settings.reverseDirection ], (treatments, outcomes, reverseDirection) => {
				let firstTreatment = treatments[0]?.label
				let secondTreatment = treatments[1]?.label || 'Control'
				if (Boolean(outcomes[0]?.isPositive) != Boolean(reverseDirection)) {
					let swapTreatment = firstTreatment
					firstTreatment = secondTreatment
					secondTreatment = swapTreatment
				}
				return (
				<div class="study-head-item study-title">
					<div class="study-condition" onclick={event =>
						expandedConditions.put(!expandedConditions.valueOf())
					}>{expandedConditions.to(expanded => expanded ? 'Patient ←' : 'P')}</div>
					<div class="study-intervention" onclick={event =>
						expandedInterventions.put(!expandedInterventions.valueOf())
					}>{expandedInterventions.to(expanded => expanded ? 'Interventions ←' : 'I')}</div>
					<div class="title-text">Title</div>
					<div class="title-favors title-favors-left" title={ 'Favors ' + firstTreatment } >
						<div class="selector-checkbox selector-selected">{ firstTreatment == 'Control' ?
							(<Icon icon={ controlIcon } />) : firstTreatment?.[0] }</div>
						← Favors
					</div>
					<div class="title-favors" title={ 'Favors ' + secondTreatment }>
						Favors →
						<div class="selector-checkbox selector-selected">{ secondTreatment == 'Control' ?
							(<Icon icon={ controlIcon } />) : secondTreatment?.[0] }</div>
					</div>
				</div>)
			})}
			<div class="study-head-item study-design">Design</div>
			<div class="study-head-item study-extract"></div>
		</header>
	</section>
)
export default StudyHeader
